<template>
  <div class="extrusion-history-modal-container animation-open-modal">
    <!-- Header Padrão SFC -->
    <AppointmentHeader
      :is-point-resource-page="true"
      :is-fixed="false"
      :resource-name="headerTitle"
      :status="orderBadgeText"
      :color="'#974900'"
      :back-event="closeModal"
    />

    <!-- Filtro de Abas no Padrão Oficial do SFC (header__tabs) -->
    <div class="header__tabs">
      <div
        v-for="tab in tabs"
        :key="tab.id"
        :class="['tab', currentTab === tab.id ? 'active' : '']"
        @click="currentTab = tab.id"
      >
        <span>{{ tab.label }}</span>
        <div class="filters-quantity">{{ getTabCount(tab.id) }}</div>
      </div>
    </div>

    <!-- Corpo Principal do Histórico (Otimizado para Tablet Touch) -->
    <div class="history-body">
      <!-- Loading State -->
      <div v-if="loading" class="history-loading">
        <b-spinner variant="warning" size="md" />
        <span class="ml-1 font-raleway font-weight-bold">Carregando histórico de apontamentos...</span>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredHistory.length === 0" class="history-empty">
        <feather-icon icon="InboxIcon" size="56" class="text-muted mb-1 opacity-5" />
        <h4 class="text-muted font-raleway font-weight-bold">Nenhum apontamento registrado</h4>
        <p class="text-muted font-raleway small">Não há apontamentos nesta categoria para a OP selecionada.</p>
      </div>

      <!-- Lista de Cards de Apontamento (Visual Limpo em Blocos Industriais) -->
      <div v-else class="history-list">
        <div
          v-for="item in filteredHistory"
          :key="`${item.appointmentType}-${item.id}`"
          class="history-card"
        >
          <!-- Top Bar: Operador + Data/Hora + Badge Status + Botão Excluir Touch -->
          <div class="card-top-bar">
            <div class="operator-profile">
              <div class="avatar-box">
                <img v-if="item.userPhotoUrl" :src="item.userPhotoUrl" alt="Avatar" />
                <span v-else>{{ avatarText(item.userName) }}</span>
              </div>
              <div class="operator-details">
                <span class="op-name">{{ item.userName || 'Operador' }}</span>
                <span class="op-time">
                  <feather-icon icon="ClockIcon" size="12" class="mr-25" />
                  {{ formatDateTime(item.eventTime) }}
                </span>
              </div>
            </div>

            <div class="top-actions">
              <!-- Badge de Status com cor real vinda do banco -->
              <span
                class="status-chip"
                :style="getStatusBadgeStyle(item.statusColor)"
              >
                {{ item.statusName || item.appointmentType }}
              </span>

              <!-- Botão Editar Apontamento -->
              <button
                v-if="canEditAppointment(item)"
                class="btn-touch-edit"
                title="Editar apontamento"
                @click="promptEdit(item)"
              >
                <feather-icon icon="Edit2Icon" size="18" />
              </button>

              <!-- Botão Excluir Otimizado para Toque no Tablet (Apenas no último apontamento e com permissão) -->
              <button
                v-if="canDeleteAppointment(item)"
                class="btn-touch-delete"
                title="Excluir o último apontamento"
                @click="promptDelete(item)"
              >
                <feather-icon icon="Trash2Icon" size="18" />
              </button>
            </div>
          </div>

          <!-- Body: Grid de Métricas / Parâmetros Técnicos -->
          <div class="card-metrics-content">
            <!-- 1. Apontamento de Finalização -->
            <div v-if="item.appointmentType === 'Finalizacao' || (item.extrusionDetails && item.extrusionDetails.situation)" class="metrics-column-grid">
              <div class="metric-cell" v-if="item.extrusionDetails && item.extrusionDetails.situation">
                <span class="m-label">Situação</span>
                <span class="m-value font-weight-bold" :style="{ color: item.statusColor || '#00cfe8' }">
                  {{ item.extrusionDetails.situation }}
                </span>
              </div>
              <div class="metric-cell" v-if="item.extrusionDetails && item.extrusionDetails.rejectionReason">
                <span class="m-label">Motivo de Reprovação</span>
                <span class="m-value font-weight-bold text-danger">{{ item.extrusionDetails.rejectionReason }}</span>
              </div>
              <div class="metric-cell" v-if="(item.extrusionDetails && item.extrusionDetails.observation) || item.observation">
                <span class="m-label">Observação</span>
                <span class="m-value">{{ (item.extrusionDetails && item.extrusionDetails.observation) || item.observation }}</span>
              </div>
            </div>

            <!-- 2. Apontamento de Extrusão / Tarugo -->
            <div v-else-if="item.extrusionDetails" class="metrics-column-grid">
              <div class="metric-cell highlight-cell-success" v-if="item.extrusionDetails.barQuantity || item.extrusionDetails.quantityBars">
                <span class="m-label">Qtd. Barras</span>
                <span class="m-value font-weight-bold text-success">{{ item.extrusionDetails.barQuantity || item.extrusionDetails.quantityBars }}</span>
              </div>
              <div class="metric-cell highlight-cell" v-if="item.extrusionDetails.quantity">
                <span class="m-label">Qtd. Tarugos</span>
                <span class="m-value font-weight-bold text-primary">{{ item.extrusionDetails.quantity }}</span>
              </div>
              <div class="metric-cell" v-if="item.extrusionDetails.sizeMm">
                <span class="m-label">Tamanho</span>
                <span class="m-value">{{ item.extrusionDetails.sizeMm }} mm</span>
              </div>
              <div class="metric-cell" v-if="item.extrusionDetails.alloy">
                <span class="m-label">Liga</span>
                <span class="m-value">{{ item.extrusionDetails.alloy }}</span>
              </div>
              <div class="metric-cell" v-if="item.extrusionDetails.heatNumber">
                <span class="m-label">Fusão / Corrida</span>
                <span class="m-value">{{ item.extrusionDetails.heatNumber }}</span>
              </div>
              <div class="metric-cell" v-if="item.extrusionDetails.origin">
                <span class="m-label">Origem</span>
                <span class="m-value">{{ item.extrusionDetails.origin }}</span>
              </div>
              <div class="metric-cell" v-if="item.extrusionDetails.pullLengthM">
                <span class="m-label">Puxada</span>
                <span class="m-value">{{ item.extrusionDetails.pullLengthM }} m</span>
              </div>
              <div class="metric-cell" v-if="item.extrusionDetails.temperature">
                <span class="m-label">Temp. Tarugo</span>
                <span class="m-value text-orange">{{ item.extrusionDetails.temperature }} °C</span>
              </div>
              <div class="metric-cell" v-if="item.extrusionDetails.emergentTemperature">
                <span class="m-label">Temp. Emergente</span>
                <span class="m-value text-red">{{ item.extrusionDetails.emergentTemperature }} °C</span>
              </div>
              <div class="metric-cell" v-if="item.extrusionDetails.extrusionSpeedMMin">
                <span class="m-label">Velocidade</span>
                <span class="m-value">{{ item.extrusionDetails.extrusionSpeedMMin }} m/min</span>
              </div>
              <div class="metric-cell" v-if="item.extrusionDetails.profileWeightKgM">
                <span class="m-label">Peso Perfil</span>
                <span class="m-value">{{ item.extrusionDetails.profileWeightKgM }} kg/m</span>
              </div>
            </div>

            <!-- 2. Apontamento de Refugo do Corte -->
            <div v-else-if="item.appointmentType === 'CorteRefugo'" class="metrics-column-grid">
              <div class="metric-cell highlight-cell-danger">
                <span class="m-label">Qtd. Peças</span>
                <span class="m-value font-weight-bold text-danger">{{ item.refuseQuantity }}</span>
              </div>
              <div class="metric-cell" v-if="item.refuseReasonName">
                <span class="m-label">Motivo de Refugo</span>
                <span class="m-value font-weight-bold">{{ item.refuseReasonName }}</span>
              </div>
            </div>

            <!-- 3. Apontamento de Cesto do Corte -->
            <div v-else-if="item.appointmentType === 'CorteCesto'" class="metrics-column-grid">
              <div
                v-for="c in item.cuttingBaskets"
                :key="c.id"
                class="basket-sub-grid"
              >
                <div class="metric-cell highlight-cell-purple">
                  <span class="m-label">Cesto</span>
                  <span class="m-value font-weight-bold text-purple">{{ c.basketCode }}</span>
                </div>
                <div class="metric-cell">
                  <span class="m-label">Qtd. Barras</span>
                  <span class="m-value font-weight-bold">{{ c.barQuantity }}</span>
                </div>
                <div class="metric-cell" v-if="c.barSizeMm">
                  <span class="m-label">Tamanho Barra</span>
                  <span class="m-value">{{ formatBarSize(c.barSizeMm) }}</span>
                </div>
              </div>
            </div>

            <!-- 4. Quantidades Gerais (Produzido / Refugado) -->
            <div v-else-if="item.producedQuantity > 0 || item.refuseQuantity > 0" class="metrics-column-grid">
              <div v-if="item.producedQuantity > 0" class="metric-cell highlight-cell">
                <span class="m-label">Produzido</span>
                <span class="m-value font-weight-bold text-success">{{ item.producedQuantity }}</span>
              </div>
              <div v-if="item.refuseQuantity > 0" class="metric-cell highlight-cell-danger">
                <span class="m-label">Refugo</span>
                <span class="m-value font-weight-bold text-danger">{{ item.refuseQuantity }}</span>
              </div>
              <div v-if="item.refuseReasonName" class="metric-cell">
                <span class="m-label">Motivo Refugo</span>
                <span class="m-value">{{ item.refuseReasonName }}</span>
              </div>
            </div>

            <!-- 5. Observação (quando houver) -->
            <div v-if="item.observation || (item.extrusionDetails && item.extrusionDetails.observation)" class="card-obs-footer">
              <feather-icon icon="MessageSquareIcon" size="14" class="mr-50 text-muted flex-shrink-0" />
              <span class="obs-text">{{ item.observation || item.extrusionDetails.observation }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Edição de Apontamento -->
    <ExtrusionEditAppointmentModal
      ref="editModal"
      :order-code="orderCode"
      :site-id="siteId"
      :order-object="orderObject"
      @saved="onEditSaved"
    />
  </div>
</template>

<script>
import { avatarText } from "@core/utils/filter";
import AppointmentHeader from '@/views/pages/operator/components/AppointmentHeader';
import { BSpinner } from 'bootstrap-vue';
import {
  getExtrusionHistory,
  deleteExtrusionProgress,
  deleteCuttingRefuse,
  deleteCuttingBasket
} from '../extrusionApi';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import ExtrusionEditAppointmentModal from './ExtrusionEditAppointmentModal.vue';

export default {
  name: 'ExtrusionHistoryModal',
  components: {
    AppointmentHeader,
    BSpinner,
    ExtrusionEditAppointmentModal
  },
  props: {
    resourceId: {
      type: [Number, String],
      required: true
    },
    resourceName: {
      type: String,
      default: ''
    },
    operationId: {
      type: [Number, String],
      required: true
    },
    orderCode: {
      type: [Number, String],
      default: ''
    },
    siteId: {
      type: [Number, String],
      default: null
    },
    orderObject: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      historyList: [],
      currentTab: 'all',
      tabs: [
        { id: 'all', label: 'Todos' },
        { id: 'tarugo', label: 'Tarugos / Extrusão' },
        { id: 'stops', label: 'Status & Paradas' },
        { id: 'cutting', label: 'Corte (Cestos & Refugos)' }
      ]
    };
  },
  computed: {
    headerTitle() {
      return `Histórico - ${this.resourceName || 'Prensa de Extrusão'}`;
    },
    orderBadgeText() {
      return this.orderCode ? `OP #${this.orderCode}` : 'Histórico';
    },
    filteredHistory() {
      if (this.currentTab === 'all') return this.historyList;
      if (this.currentTab === 'tarugo') {
        return this.historyList.filter(
          item => item.appointmentType === 'Tarugo' || item.appointmentType === 'Finalizacao' || item.extrusionDetails != null
        );
      }
      if (this.currentTab === 'stops') {
        return this.historyList.filter(
          item => item.appointmentType === 'Parada' || item.appointmentType === 'Setup' || item.appointmentType === 'Execucao'
        );
      }
      if (this.currentTab === 'cutting') {
        return this.historyList.filter(
          item => item.appointmentType === 'CorteRefugo' || item.appointmentType === 'CorteCesto'
        );
      }
      return this.historyList;
    },
    currentUser() {
      try {
        const storeUser = this.$store?.state?.auth?.userInfo?.userData;
        if (storeUser && (storeUser.guid || storeUser.Guid || storeUser.id)) return storeUser;

        const cookieUser = this.$cookies?.get?.('userInfo')?.userData;
        if (cookieUser && (cookieUser.guid || cookieUser.Guid || cookieUser.id)) return cookieUser;

        const raw = localStorage.getItem('userData');
        return raw ? JSON.parse(raw) : {};
      } catch (e) {
        return {};
      }
    },
    isSuperiorUser() {
      const u = this.currentUser;
      if (!u) return false;

      // Admin check: suporte a boolean true, string "1", number 1 ou action "manage"
      const isAdmin = u.isAdmin === true || u.is_admin === true || u.is_admin === '1' || u.is_admin === 1 ||
        (Array.isArray(u.ability) && u.ability.some(a => a.action === 'manage'));
      if (isAdmin) return true;

      // PCP check: suporte a boolean true, string "1", number 1 ou subject "pcp"
      const isPcp = u.pcpAccess === true || u.pcp_access === true || u.pcp_access === '1' || u.pcp_access === 1 ||
        (Array.isArray(u.ability) && u.ability.some(a => a.subject === 'pcp'));
      if (isPcp) return true;

      // Leadership check: cargos com papel de liderança
      const role = (u.jobRole || u.job_role || '').toLowerCase();
      const leadershipKeywords = ['supervisor', 'lider', 'líder', 'gerente', 'coordenador', 'diretor', 'gestor', 'chefe'];
      return leadershipKeywords.some(k => role.includes(k));
    }
  },
  mounted() {
    this.fetchHistory();
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    async fetchHistory() {
      if (!this.operationId || !this.resourceId) return;
      this.loading = true;
      try {
        const response = await getExtrusionHistory({
          operationId: this.operationId,
          resourceId: this.resourceId
        });
        this.historyList = response?.data?.data || [];
      } catch (err) {
        console.error('Erro ao buscar histórico de extrusão:', err);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            icon: 'AlertTriangleIcon',
            text: 'Não foi possível carregar o histórico de apontamentos.',
            variant: 'danger'
          }
        });
      } finally {
        this.loading = false;
      }
    },
    getTabCount(tabId) {
      if (tabId === 'all') return this.historyList.length;
      if (tabId === 'tarugo') {
        return this.historyList.filter(
          item => item.appointmentType === 'Tarugo' || item.appointmentType === 'Finalizacao' || item.extrusionDetails != null
        ).length;
      }
      if (tabId === 'stops') {
        return this.historyList.filter(
          item => item.appointmentType === 'Parada' || item.appointmentType === 'Setup' || item.appointmentType === 'Execucao'
        ).length;
      }
      if (tabId === 'cutting') {
        return this.historyList.filter(
          item => item.appointmentType === 'CorteRefugo' || item.appointmentType === 'CorteCesto'
        ).length;
      }
      return 0;
    },
    avatarText,
    formatDateTime(dateVal) {
      if (!dateVal) return '-';
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return String(dateVal);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} às ${hours}:${minutes}`;
    },
    getStatusBadgeStyle(color) {
      const rawColor = (color || '#61b3fa').trim();
      let bg = `${rawColor}1A`;
      if (rawColor.startsWith('#')) {
        let hex = rawColor.replace('#', '');
        if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
        if (hex.length >= 6) {
          const r = parseInt(hex.substring(0, 2), 16);
          const g = parseInt(hex.substring(2, 4), 16);
          const b = parseInt(hex.substring(4, 6), 16);
          if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
            bg = `rgba(${r}, ${g}, ${b}, 0.12)`;
          }
        }
      }
      return {
        backgroundColor: bg,
        color: rawColor,
        border: `1px solid ${rawColor}40`,
        borderRadius: '8px',
        fontWeight: '700',
        padding: '5px 14px',
        fontSize: '13px',
        lineHeight: '16px',
        fontFamily: '"Raleway", sans-serif',
        whiteSpace: 'nowrap'
      };
    },
    hasPermissionForAppointment(item) {
      if (!item) return false;
      if (this.isSuperiorUser) return true;

      const currentGuid = (this.currentUser.guid || this.currentUser.Guid || this.currentUser.id || this.currentUser.Id || '').toLowerCase();
      if (!currentGuid) return false;

      let ownerGuid = item.userGuid;
      if (item.appointmentType === 'CorteCesto' && item.cuttingBaskets && item.cuttingBaskets.length > 0) {
        ownerGuid = item.cuttingBaskets[0].userGuid || ownerGuid;
      } else if (item.appointmentType === 'CorteRefugo' && item.cuttingRefuses && item.cuttingRefuses.length > 0) {
        ownerGuid = item.cuttingRefuses[0].userGuid || ownerGuid;
      }

      // Se não há guid gravado (ex: dados legados), permite para manter compatibilidade
      if (!ownerGuid || ownerGuid === '00000000-0000-0000-0000-000000000000') return true;

      return ownerGuid.toLowerCase() === currentGuid;
    },
    canDeleteAppointment(item) {
      if (!this.isLatestAppointment(item)) return false;
      return this.hasPermissionForAppointment(item);
    },
    promptEdit(item) {
      if (!item || !this.$refs.editModal) return;
      if (!this.hasPermissionForAppointment(item)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Acesso Negado',
            icon: 'AlertTriangleIcon',
            text: 'Apenas o autor do apontamento ou liderança podem editar este registro.',
            variant: 'warning'
          }
        });
        return;
      }
      this.$refs.editModal.open(item);
    },
    async onEditSaved() {
      await this.fetchHistory();
      this.$emit('refresh');
    },
    canEditAppointment(item) {
      if (!item) return false;
      if (!this.hasPermissionForAppointment(item)) return false;

      // 1. "Iniciado" (StatusId = 1) não deve ser editado
      if (item.statusId === 1 || (item.statusName || '').trim().toLowerCase() === 'iniciado') {
        return false;
      }

      // 2. "Finalizado" / "Operação Finalizada" (StatusId = 5) não deve ser editado
      if (item.appointmentType === 'Finalizacao' || item.statusId === 5 || (item.statusName || '').toLowerCase().includes('finaliz')) {
        return false;
      }

      // 3. "Executando" / "Execução" simples (StatusId = 3 sem detalhes de Tarugo) NÃO deve ser editado
      if (item.statusId === 3 || (item.statusName || '').trim().toLowerCase() === 'executando' || item.appointmentType === 'Execucao') {
        // Só permite editar se for um apontamento de Tarugo com detalhes da prensa
        if (item.extrusionDetails != null || item.appointmentType === 'Tarugo') {
          return true;
        }
        return false;
      }

      // 4. Cesto de Corte: se já estiver em forno (InFurnace ou furnaceChargeId), bloqueia edição
      if (item.appointmentType === 'CorteCesto') {
        const basket = item.cuttingBaskets && item.cuttingBaskets[0];
        if (basket && (basket.status === 'InFurnace' || basket.furnaceChargeId)) {
          return false;
        }
        return true;
      }

      // 5. Refugo de Corte: pode ser editado
      if (item.appointmentType === 'CorteRefugo') {
        return true;
      }

      // 6. Tarugos (apontamento de prensa): pode ser editado
      if (item.extrusionDetails != null || item.appointmentType === 'Tarugo') {
        return true;
      }

      // 7. Todos os demais status de Parada / Setup (ex: Problema Elétrico, Setup, Manutenção, etc.): podem ser editados!
      if (item.statusId && item.statusId !== 1 && item.statusId !== 3 && item.statusId !== 5) {
        return true;
      }

      return false;
    },
    isLatestAppointment(item) {
      if (!Array.isArray(this.historyList) || this.historyList.length === 0 || !item) return false;
      const latest = this.historyList[0];
      return latest && latest.id === item.id && latest.appointmentType === item.appointmentType;
    },
    async promptDelete(item) {
      if (!item || !this.canDeleteAppointment(item)) return;
      if (!this.hasPermissionForAppointment(item)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Acesso Negado',
            icon: 'AlertTriangleIcon',
            text: 'Apenas o autor do apontamento ou liderança podem excluir este registro.',
            variant: 'warning'
          }
        });
        return;
      }

      const res = await this.$swal({
        title: 'Excluir Apontamento?',
        text: `Deseja realmente excluir este apontamento de ${item.statusName || item.appointmentType}? Essa ação não poderá ser desfeita.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, Excluir',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1'
        },
        buttonsStyling: false
      });

      if (res && (res.isConfirmed || res.value === true)) {
        await this.executeDelete(item);
      }
    },
    async executeDelete(item) {
      if (!item) return;
      try {
        if (item.appointmentType === 'CorteRefugo') {
          await deleteCuttingRefuse(item.id);
        } else if (item.appointmentType === 'CorteCesto') {
          await deleteCuttingBasket(item.id);
        } else {
          await deleteExtrusionProgress(item.id);
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso',
            icon: 'CheckIcon',
            text: 'Apontamento excluído com sucesso!',
            variant: 'success'
          }
        });

        // Recarregar histórico e avisar painel pai
        await this.fetchHistory();
        this.$emit('refresh');
      } catch (err) {
        console.error('Erro ao excluir apontamento:', err);
        const errorMsg = err.response?.data?.message || 'Não foi possível excluir o apontamento.';
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            icon: 'AlertTriangleIcon',
            text: errorMsg,
            variant: 'danger'
          }
        });
      }
    },
    formatBarSize(size) {
      if (!size) return '-';
      let num = Number(size);
      if (isNaN(num) || num <= 0) return '-';
      if (num <= 50) num *= 1000;
      return `${num.toFixed(0)} mm`;
    }
  }
};
</script>

<style lang="scss" scoped>
.extrusion-history-modal-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1050;
  background: #f6f7f9;
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  overflow: hidden;

  /* Header Tabs no padrão SFC */
  .header__tabs {
    display: flex;
    justify-content: center;
    width: 100%;
    background: #fff;
    flex-shrink: 0;
    box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.06);

    .tab {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      color: #998f8a;
      font-size: 16px;
      font-weight: 600;
      line-height: 24px;
      text-align: center;
      cursor: pointer;
      width: 100%;
      border-bottom: 1px solid #cfc4be;
      padding: 14px 16px;
      transition: all 0.2s ease;
      user-select: none;

      .filters-quantity {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        color: #fff;
        min-width: 20px;
        height: 20px;
        padding: 0 6px;
        border-radius: 20px;
        background: #998f8a;
        font-size: 11px;
        font-weight: 700;
        line-height: 14px;
        transition: background 0.2s ease;
      }

      &.active {
        color: #974900;
        border-color: #a35205;
        box-shadow: 0px -4px 0px 0px #974900 inset;

        .filters-quantity {
          background: #974900;
        }
      }

      &:hover:not(.active) {
        color: #4c4541;
        background: #faf8f6;
      }
    }
  }

  /* Corpo com rolagem suave e largura total de tela no tablet */
  .history-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 20px 24px;
    overflow-y: auto;
    width: 100%;
    max-width: 1300px;
    margin: 0 auto;
  }

  .history-loading,
  .history-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 0;
    text-align: center;
  }

  .history-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-bottom: 24px;
  }

  /* Card de Histórico Industrial */
  .history-card {
    background: #ffffff;
    border: 1px solid #d9d4d0;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;

    &:hover {
      border-color: #b5a9a1;
      box-shadow: 0 4px 14px rgba(89, 41, 0, 0.08);
    }

    /* Barra Superior do Card: Operador + Badge + Botão Lixeira */
    .card-top-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 18px;
      background: #faf9f8;
      border-bottom: 1px solid #ede8e4;
      flex-wrap: wrap;
      gap: 10px;

      .operator-profile {
        display: flex;
        align-items: center;
        gap: 12px;

        .avatar-box {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #ffede2;
          border: 1px solid #ffd8c2;
          color: #974900;
          font-weight: 700;
          font-size: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          flex-shrink: 0;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }

        .operator-details {
          display: flex;
          flex-direction: column;

          .op-name {
            font-family: "Raleway", sans-serif;
            font-size: 15px;
            font-weight: 700;
            color: #4c4541;
            line-height: 18px;
          }

          .op-time {
            font-size: 12px;
            color: #7e7570;
            display: inline-flex;
            align-items: center;
            margin-top: 2px;
          }
        }
      }

      .top-actions {
        display: flex;
        align-items: center;
        gap: 12px;

        .btn-touch-edit {
          width: 38px;
          height: 38px;
          border-radius: 8px;
          border: 1px solid #ffe0b2;
          background: #fff;
          color: #974900;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover,
          &:active {
            background: #fff3e0;
            border-color: #974900;
            color: #7a3b00;
            transform: scale(1.05);
          }
        }

        .btn-touch-delete {
          width: 38px;
          height: 38px;
          border-radius: 8px;
          border: 1px solid #ffcdd2;
          background: #fff;
          color: #d32f2f;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover,
          &:active {
            background: #ffebee;
            border-color: #d32f2f;
            color: #b71c1c;
            transform: scale(1.05);
          }
        }
      }
    }

    /* Conteúdo do Card: Grid Limpo e Espaçoso de Colunas */
    .card-metrics-content {
      padding: 14px 18px;
      display: flex;
      flex-direction: column;
      gap: 10px;

      .metrics-column-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
        gap: 10px;
        align-items: stretch;

        .basket-sub-grid {
          display: contents;
        }

        .metric-cell {
          background: #fdfdfd;
          border: 1px solid #e7e2de;
          border-radius: 8px;
          padding: 8px 12px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          min-height: 52px;

          .m-label {
            font-family: "Raleway", sans-serif;
            font-size: 10.5px;
            font-weight: 700;
            color: #7e7570;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            line-height: 13px;
            margin-bottom: 3px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .m-value {
            font-family: "Raleway", sans-serif;
            font-size: 15px;
            font-weight: 600;
            color: #4c4541;
            line-height: 18px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          &.highlight-cell {
            background: #f0f7ff;
            border-color: #b8daff;

            .m-label {
              color: #0056b3;
            }

            .m-value {
              font-size: 17px;
              color: #0056b3;
            }
          }

          &.highlight-cell-success {
            background: #f0fdf4;
            border-color: #bbf7d0;

            .m-label {
              color: #166534;
            }

            .m-value {
              font-size: 17px;
              color: #28c76f;
            }
          }

          &.highlight-cell-danger {
            background: #fff5f5;
            border-color: #f5c6cb;

            .m-label {
              color: #721c24;
            }

            .m-value {
              font-size: 17px;
              color: #dc3545;
            }
          }

          &.highlight-cell-purple {
            background: #f8f5ff;
            border-color: #d6c7ff;

            .m-label {
              color: #5a32a3;
            }

            .m-value {
              font-size: 17px;
              color: #7367f0;
            }
          }
        }
      }

      /* Rodapé de Observações */
      .card-obs-footer {
        display: flex;
        align-items: center;
        background: #faf8f5;
        border: 1px solid #eee7e0;
        border-radius: 6px;
        padding: 8px 12px;
        margin-top: 4px;
        font-size: 13px;
        color: #594f47;

        .obs-text {
          word-break: break-word;
        }
      }
    }
  }
}

.text-orange {
  color: #e65100 !important;
}

.text-red {
  color: #d84315 !important;
}

.text-purple {
  color: #7367f0 !important;
}

/* Responsividade para Tablet / Touch */
@media (max-width: 992px) {
  .extrusion-history-modal-container {
    .history-body {
      padding: 14px 16px;
    }

    .history-card {
      .card-metrics-content {
        .metrics-column-grid {
          grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
          gap: 8px;

          .metric-cell {
            padding: 6px 10px;

            .m-value {
              font-size: 14px;
            }
          }
        }
      }
    }
  }
}

@media (max-width: 576px) {
  .extrusion-history-modal-container {
    .header__tabs {
      .tab {
        font-size: 13px;
        padding: 10px 6px;
        gap: 6px;
      }
    }

    .history-card {
      .card-metrics-content {
        .metrics-column-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }
    }
  }
}
</style>
